import React from "react";
import { X } from "lucide-react";
import { Row, kmToMi } from "./shared";

/** Popover that appears on the Admin map when an operator clicks a marker.
 *  Renders driver details for driver pins, or full ride details + Route Preview
 *  metrics for pickup/dropoff pins. Close + Clear-route both dismiss. */
export const MarkerInfoCard = ({ marker, drivers, routePreview, onClose }) => {
  if (!marker) return null;
  const data = marker.payload || {};

  let title = marker.label;
  let subtitle = "";
  let body = null;
  let accent = "#0A0A0A";

  if (marker.kind === "driver") {
    accent = data.is_online ? "#10B981" : "#9CA3AF";
    title = data.name;
    subtitle = `${data.vehicle_make || "—"} · ${data.vehicle_plate || "—"}`;
    body = (
      <>
        <Row label="STATUS" value={data.is_online ? "ONLINE" : "OFFLINE"} valueColor={accent} />
        <Row label="PHONE" value={data.phone || "—"} />
        <Row label="RATING" value={data.avg_rating ? `${data.avg_rating} (${data.rating_count || 0})` : "—"} />
        <Row label="LAST SEEN" value={data.last_seen ? new Date(data.last_seen).toLocaleString() : "—"} />
      </>
    );
  } else if (marker.kind === "pickup" || marker.kind === "dropoff") {
    accent = marker.kind === "pickup" ? "#10B981" : "#EF4444";
    title = data.passenger_name;
    subtitle = marker.kind === "pickup" ? "PICKUP" : "DROP-OFF";
    const driverName = data.driver_id
      ? (drivers.find((d) => d.id === data.driver_id)?.name || "—")
      : null;
    const tripMi = routePreview ? kmToMi(routePreview.tripKm).toFixed(1) : null;
    const tripMin = routePreview ? Math.max(1, Math.round(routePreview.tripMin)) : null;
    const pickupMi = routePreview?.driverToPickupKm != null
      ? kmToMi(routePreview.driverToPickupKm).toFixed(1)
      : null;
    const pickupMin = routePreview?.driverToPickupMin != null
      ? Math.max(1, Math.round(routePreview.driverToPickupMin))
      : null;

    body = (
      <>
        <Row label="FROM" value={data.pickup_address} />
        <Row label="TO" value={data.dropoff_address} />
        <Row label="FARE" value={`£${(data.fare ?? data.estimated_fare ?? 0).toFixed ? (data.fare ?? data.estimated_fare ?? 0).toFixed(2) : (data.fare ?? data.estimated_fare ?? 0)}`} />
        <Row label="STATUS" value={(data.status || "").replace("_", " ").toUpperCase()} />
        {driverName && <Row label="DRIVER" value={driverName} />}
        {!driverName && data.offered_to && (
          <Row label="OFFERED TO" value={drivers.find((d) => d.id === data.offered_to)?.name || "—"} />
        )}
        <Row label="PAYMENT" value={(data.payment_method || "").toUpperCase()} />

        {/* Route Preview metrics — only shown for ride markers, populated by routePreview */}
        {routePreview && (
          <div
            className="mt-3 pt-3 border-t border-border space-y-1.5"
            data-testid="route-preview-metrics"
          >
            <div className="label-mini text-muted-foreground">ROUTE PREVIEW</div>
            <Row
              label="PICKUP → DROP-OFF"
              value={`${tripMi} mi · ${tripMin} min`}
              valueColor="#0A0A0A"
            />
            {pickupMi != null && (
              <Row
                label="DRIVER → PICKUP"
                value={`${pickupMi} mi · ${pickupMin} min`}
                valueColor="#92400E"
              />
            )}
            {routePreview.assignedDriver && (
              <div className="text-[10px] text-muted-foreground italic">
                {routePreview.assignedDriver.name} is{" "}
                {routePreview.assignedDriver.is_online ? "tracking live" : "currently offline"}.
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className="absolute bottom-3 left-3 right-3 md:right-auto md:w-80 bg-white border-2 z-20 shadow-xl"
      style={{ borderColor: accent }}
      data-testid="map-marker-info-card"
    >
      <div className="flex items-start justify-between p-3 border-b border-border">
        <div className="min-w-0">
          <div className="label-mini text-muted-foreground">{subtitle}</div>
          <div className="font-heading text-lg font-black tracking-tight truncate">{title}</div>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground p-1 flex-shrink-0"
          aria-label="Close info card"
          data-testid="marker-info-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-3 space-y-2">{body}</div>
      {routePreview && (marker.kind === "pickup" || marker.kind === "dropoff") && (
        <div className="border-t border-border p-2">
          <button
            onClick={onClose}
            className="w-full text-xs uppercase tracking-wider font-bold py-1.5 border border-border hover:bg-secondary transition-colors"
            data-testid="clear-route-btn"
          >
            Clear route
          </button>
        </div>
      )}
    </div>
  );
};
